import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Page,
  Layout,
  Filters,
  EmptyState,
  Spinner,
  Button,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import DataTable from "components/datatable/Datatable";
import useIsPhone from "features/files/use-is-phone";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import ProductGroupForm from "./product-group-form";
import { PHONE_PAGE_GUTTER } from "components/tw/pageGutter";
import {
  fetch as fetchProductGroupsApi,
  fetchById as fetchProductGroupById,
} from "services/product-groups.service";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

type ProductGroup = {
  id: string;
  name?: string;
  code?: string | null;
  description?: string;
  products?: any[];
};

const formatProductLabel = (product: any) => {
  const codes = [product?.sku, product?.supplierSku].filter(Boolean);
  const codesSuffix = codes.length ? ` [${codes.join(" / ")}]` : "";
  return `${product?.title || ""}${codesSuffix}`;
};

function ProductGroups() {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const isPhone = useIsPhone();

  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // first page / reset
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchInput, setSearchInput] = useState(""); // controlled input
  const [searchQuery, setSearchQuery] = useState(""); // debounced, applied

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroup, setEditingGroup] = useState<ProductGroup | null>(null);

  const headerRef = useRef<HTMLDivElement>(null); // search region (ResizeObserver)
  const listWrapRef = useRef<HTMLDivElement>(null); // wraps the table: measures top + hosts the scroll listener
  const sentinelRef = useRef<HTMLDivElement>(null); // infinite-scroll sentinel (mobile)
  const requestSeqRef = useRef(0); // ignore stale responses (no cancel token)
  const [listMaxHeight, setListMaxHeight] = useState("calc(100vh - 280px)");

  // Debounce the typed search into the applied query.
  useEffect(() => {
    const handle = setTimeout(
      () => setSearchQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Cap the table body height so only the list scrolls and the column headers
  // stick (uses DataTable's maxBodyHeight; the title/search stay fixed).
  useLayoutEffect(() => {
    const compute = () => {
      const el = listWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Reserve room below the table for the loading-more / end-of-list footer.
      const available = window.innerHeight - top - 48;
      setListMaxHeight(`${Math.max(Math.round(available), 240)}px`);
    };

    compute();
    window.addEventListener("resize", compute);

    let resizeObserver: ResizeObserver | undefined;
    if (headerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(compute);
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", compute);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const loadGroups = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (!companyId) return;
      const seq = ++requestSeqRef.current;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setIsError(false);

      try {
        const filtersValue = searchQuery ? { textSearch: searchQuery } : {};
        const data = await fetchProductGroupsApi(companyId, filtersValue, {
          page: pageToLoad,
          pageSize: PAGE_SIZE,
        });
        if (seq !== requestSeqRef.current) return; // superseded by newer request

        const incoming: ProductGroup[] = data.productGroupsCollection || [];
        const pagination = data.pagination || {};

        setGroups((prev) => {
          if (!append) return incoming;
          const seen = new Set(prev.map((item) => item.id));
          return [...prev, ...incoming.filter((item) => !seen.has(item.id))];
        });
        setHasNextPage(Boolean(pagination.hasNextPage));
        setPage(pagination.page || pageToLoad);
      } catch (e) {
        if (seq !== requestSeqRef.current) return;
        setIsError(true);
      } finally {
        if (seq === requestSeqRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [companyId, searchQuery]
  );

  // Initial load + reload whenever company or the applied search changes.
  useEffect(() => {
    setGroups([]);
    setPage(1);
    setHasNextPage(false);
    setIsLoading(true);
    loadGroups(1, false);
  }, [loadGroups]);

  // Table infinite scroll: listen to the DataTable's internal scroll area
  // (the element that also makes the header sticky).
  useEffect(() => {
    if (isPhone) return undefined;
    const wrap = listWrapRef.current;
    if (!wrap) return undefined;
    const scrollEl = wrap.querySelector<HTMLElement>(".overflow-y-auto");
    if (!scrollEl) return undefined;

    const onScroll = () => {
      if (isLoading || isLoadingMore || !hasNextPage) return;
      if (
        scrollEl.scrollTop + scrollEl.clientHeight >=
        scrollEl.scrollHeight - 200
      ) {
        loadGroups(page + 1, true);
      }
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // auto-fill if the first page doesn't fill the viewport
    return () => scrollEl.removeEventListener("scroll", onScroll);
  }, [
    isPhone,
    hasNextPage,
    isLoading,
    isLoadingMore,
    page,
    loadGroups,
    groups.length,
  ]);

  // Phone infinite scroll: cards flow in the page, so observe a sentinel.
  useEffect(() => {
    if (!isPhone) return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasNextPage &&
          !isLoading &&
          !isLoadingMore
        ) {
          loadGroups(page + 1, true);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isPhone, hasNextPage, isLoading, isLoadingMore, page, loadGroups]);

  const handleQueryClear = () => setSearchInput("");

  const appliedFilters = searchQuery
    ? [
      {
        key: "textSearch",
        label: `${t("search")}: ${searchQuery}`,
        onRemove: handleQueryClear,
      },
    ]
    : [];

  const handleCreate = useCallback(() => {
    setEditingGroupId(null);
    setEditingGroup(null);
    setIsFormOpen(true);
  }, []);

  const handleEdit = useCallback((item: ProductGroup) => {
    setEditingGroupId(item.id);
    setEditingGroup(item);
    setIsFormOpen(true);
  }, []);

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingGroupId(null);
    setEditingGroup(null);
  };

  // Replace a single already-loaded group in place (no-op if not loaded).
  const upsertGroup = (group: ProductGroup) => {
    if (!group || !group.id) return;
    setGroups((prev) => {
      const idx = prev.findIndex((g) => g.id === group.id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = group;
      return next;
    });
  };

  // On edit: refresh just that row (keeps loaded pages + scroll position).
  // On create (no id): reload the first page so the new group surfaces.
  const handleSaved = async (savedGroupId?: string) => {
    if (!savedGroupId || !companyId) {
      loadGroups(1, false);
      return;
    }
    try {
      const { productGroupEntity } = await fetchProductGroupById(
        companyId,
        savedGroupId
      );
      if (productGroupEntity) upsertGroup(productGroupEntity);
      else loadGroups(1, false);
    } catch (e) {
      // Best-effort: on failure the row keeps its previously displayed values.
    }
  };

  const tableCollection = groups.map((group: any) => {
    const products = Array.isArray(group.products) ? group.products : [];
    const MAX_VISIBLE = 6;
    const visible = products.slice(0, MAX_VISIBLE);
    const remaining = products.length - visible.length;

    const productsNode =
      products.length === 0 ? (
        <span className="text-gray-500">-</span>
      ) : (
        <div className="text-xs leading-5">
          {visible.map((p: any) => (
            <div
              key={p?.id || formatProductLabel(p)}
              className="min-w-0 [overflow-wrap:anywhere] md:whitespace-nowrap"
            >
              {formatProductLabel(p)}
            </div>
          ))}
          {remaining > 0 && (
            <div className="text-gray-500 md:whitespace-nowrap">+{remaining}</div>
          )}
        </div>
      );

    return {
      ...group,
      productsList: productsNode,
    };
  });

  const tableColumns: Column[] = [
    { key: "#" },
    { key: "code", name: t("code"), onClick: handleEdit },
    { key: "name", name: t("name"), onClick: handleEdit },
    { key: "productsList", name: t("products"), onClick: handleEdit },
    { key: "description", name: t("description"), onClick: handleEdit },
  ];

  // On a phone each group is a card read top-down, left-aligned: the name with
  // the code beside it, then the products and the description, each under its
  // label. The whole card opens the group (`onRowClick` below), so none of
  // these cells is a button of its own.
  const phoneColumns: Column[] = [
    {
      key: "name",
      name: t("name"),
      type: "custom",
      mobileView: "topLeft",
      render: (group: any) => (
        <span className="font-medium text-gray-900 [overflow-wrap:anywhere]">
          {group.name}
        </span>
      ),
    },
    {
      key: "code",
      name: t("code"),
      type: "custom",
      mobileView: "topRight",
      render: (group: any) => (
        <span className="pl-3 text-sm text-gray-500">{group.code}</span>
      ),
    },
    {
      key: "details",
      type: "custom",
      mobileView: "bottomLeft",
      render: (group: any) => (
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-xs text-gray-500">{t("products")}</dt>
            <dd>{group.productsList}</dd>
          </div>
          {group.description && (
            <div>
              <dt className="text-xs text-gray-500">{t("description")}</dt>
              <dd className="[overflow-wrap:anywhere]">{group.description}</dd>
            </div>
          )}
        </dl>
      ),
    },
  ];

  const columns = isPhone ? phoneColumns : tableColumns;

  const showSkeleton = isLoading && groups.length === 0;
  const showEmpty = !isLoading && !isError && groups.length === 0;

  return (
    <Layout>
      <Layout.Section>
        <Page
          fullWidth
          primaryAction={{
            content: t("create_product_group"),
            onAction: handleCreate,
          }}
          title={t("product_groups")}
        >
          <div ref={headerRef} style={{ padding: "16px", display: "flex" }}>
            <div style={{ flex: 1 }}>
              <Filters
                queryValue={searchInput}
                queryPlaceholder={t("search")}
                filters={[]}
                appliedFilters={appliedFilters}
                onQueryChange={setSearchInput}
                onQueryClear={handleQueryClear}
                onClearAll={handleQueryClear}
              />
            </div>
          </div>

          <div ref={listWrapRef} className={PHONE_PAGE_GUTTER}>
            {showSkeleton ? (
              <DataTableSkeleton rows={10} columns={columns.length} />
            ) : isError ? (
              <EmptyState
                heading={t("not_fetch", {
                  defaultValue: "Could not load product groups",
                })}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <Button onClick={() => loadGroups(1, false)}>
                  {t("retry", { defaultValue: "Retry" })}
                </Button>
              </EmptyState>
            ) : showEmpty ? (
              <EmptyState
                heading={t("no_product_groups", {
                  defaultValue: "No product groups found",
                })}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              />
            ) : (
              <>
                <DataTable
                  collection={tableCollection}
                  columns={columns}
                  onRowClick={isPhone ? handleEdit : undefined}
                  isTotalsDisabled
                  maxBodyHeight={isPhone ? undefined : listMaxHeight}
                />
                {/* Sentinel for phone infinite scroll (page-level scroll) */}
                <div ref={sentinelRef} style={{ height: 1 }} />

                {isLoadingMore && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "12px",
                    }}
                  >
                    <Spinner
                      accessibilityLabel={t("loading", {
                        defaultValue: "Loading",
                      })}
                      size="small"
                    />
                  </div>
                )}

                {!hasNextPage && !isLoadingMore && groups.length > 0 && (
                  <div style={{ textAlign: "center", padding: "12px" }}>
                    <Text as="span" tone="subdued" variant="bodySm">
                      {t("all_caught_up", {
                        defaultValue: "No more product groups",
                      })}
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>

          <ProductGroupForm
            companyId={companyId || ""}
            groupId={editingGroupId}
            initialGroup={editingGroup}
            open={isFormOpen}
            onClose={handleFormClose}
            onSaved={handleSaved}
          />
        </Page>
      </Layout.Section>
    </Layout>
  );
}

export default ProductGroups;


